import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  RefreshCw,
  Search,
  Settings2,
  Truck,
} from 'lucide-react';
import { previewCoverageOf } from './vehicleModels/domain/coveragePreview';
import { findPresetForRenderIdentity } from './vehicleModels/presets/registry';
import { layoutGroupsOf } from './vehicleModels/domain/setupLayoutPicker';
import SetupRenderPreview from './SetupRenderPreview';
import FormField from './FormField';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Skeleton } from './ui/skeleton';

const coverageLabel = (coverage) => coverage?.state === 'exact_3d'
  ? 'Interactive 3D'
  : coverage?.state === 'configured_2d'
    ? 'Exact 2D position map'
    : 'Coverage pending';

const SetupLayoutPicker = ({
  layouts,
  loading,
  loadError,
  selectedClass,
  selectedLayout,
  selectedVehicleCount,
  mixedConfirmedClasses,
  reason,
  error,
  success,
  successAction,
  submitting,
  onClassSelect,
  onLayoutSelect,
  onReasonChange,
  onApply,
  onRetryLoad,
}) => {
  const [classSearch, setClassSearch] = useState('');
  const groups = useMemo(() => layoutGroupsOf(layouts), [layouts]);
  const query = classSearch.trim().toLowerCase();
  const visibleGroups = query
    ? groups.filter((group) => `${group.name} ${group.key}`.toLowerCase().includes(query))
    : groups;
  const activeGroup = groups.find((group) => group.key === selectedClass);
  const activeLayout = activeGroup?.layouts.find((layout) => layout.id === selectedLayout);

  return (
    <div className="flex w-full max-w-[440px] shrink-0 flex-col gap-5">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>2. Confirm vehicle class</CardTitle>
              <p className="mt-1 text-sm text-slate-500">Choose the body family before its axle layout.</p>
            </div>
            {!loading && <span className="tm-catalog-count">{groups.length} classes</span>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={classSearch}
              onChange={(event) => setClassSearch(event.target.value)}
              className="pl-9"
              placeholder="Find a vehicle class"
              aria-label="Find a vehicle class"
              disabled={loading}
            />
          </div>
          {loading && <div className="space-y-2">
            {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} height={58} />)}
          </div>}
          {!loading && loadError && <div className="space-y-3">
            <div className="tm-picker-notice tm-picker-notice--danger">
              <AlertCircle className="h-4 w-4" />
              <span>{loadError.message || loadError || 'Published layouts could not be loaded.'}</span>
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={onRetryLoad}>
              <RefreshCw className="h-4 w-4" /> Retry loading layouts
            </Button>
          </div>}
          {!loading && !loadError && <div className="tm-class-rail" role="radiogroup" aria-label="Vehicle class">
            {visibleGroups.map((group) => {
              const checked = selectedClass === group.key;
              return <button
                type="button"
                role="radio"
                aria-checked={checked}
                key={group.key}
                data-selected={checked || undefined}
                className="tm-class-choice"
                onClick={() => onClassSelect(group.key)}
              >
                <span className="tm-class-choice__mark"><Truck aria-hidden="true" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-extrabold text-slate-950">{group.name}</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {group.layouts.length} axle layout{group.layouts.length === 1 ? '' : 's'}
                  </span>
                </span>
                {checked ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              </button>;
            })}
            {!visibleGroups.length && <p className="p-4 text-sm text-slate-500">No vehicle class matches “{classSearch}”.</p>}
          </div>}
          {!loading && !loadError && groups.length <= 3 && <p className="mt-3 text-xs leading-5 text-amber-700">
            Only the original catalog is available. Apply backend render-catalog migrations to publish the expanded classes.
          </p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>3. Choose axle layout</CardTitle>
          <p className="text-sm text-slate-500">
            {activeGroup ? `Showing layouts approved for ${activeGroup.name}.` : 'Confirm a vehicle class to see its layouts.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-3" role="radiogroup" aria-label="Axle layout">
          {!activeGroup && <div className="tm-picker-empty">
            <Truck className="h-5 w-5" />
            <span>No class selected</span>
          </div>}
          {activeGroup?.layouts.map((layout) => {
            const coverage = previewCoverageOf(layout, findPresetForRenderIdentity);
            const checked = selectedLayout === layout.id;
            return <button
              type="button"
              key={layout.id}
              role="radio"
              aria-checked={checked}
              data-selected={checked || undefined}
              onClick={() => onLayoutSelect(layout.id)}
              className="tm-layout-choice"
            >
              <span className="tm-layout-choice__wheel-count">{layout.wheel_count}</span>
              <span className="min-w-0 flex-1">
                <span className="block font-extrabold text-slate-950">{layout.name}</span>
                <span className="mt-1 block text-xs text-slate-500">v{layout.version} · {coverageLabel(coverage)}</span>
              </span>
              {checked && <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />}
            </button>;
          })}
        </CardContent>
      </Card>

      {activeLayout && <Card>
        <CardHeader>
          <CardTitle>Render preview</CardTitle>
          <p className="text-sm text-slate-500">{activeGroup.name} · {activeLayout.wheel_count} tyre positions</p>
        </CardHeader>
        <CardContent><SetupRenderPreview layout={activeLayout} /></CardContent>
      </Card>}

      <Card>
        <CardHeader><CardTitle>4. Review and apply</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {mixedConfirmedClasses && <div className="tm-picker-notice tm-picker-notice--danger">
            <AlertCircle className="h-4 w-4" />
            <span>The selected vehicles have different confirmed classes. Configure each class separately.</span>
          </div>}
          <div className="tm-configuration-ticket">
            <p className="tm-configuration-ticket__label">Assignment</p>
            <p className="mt-2 text-lg font-extrabold text-slate-950">
              {selectedVehicleCount} vehicle{selectedVehicleCount === 1 ? '' : 's'}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {activeGroup?.name || 'No class selected'}
              {activeLayout ? ` · ${activeLayout.name}` : ''}
            </p>
          </div>
          <FormField label="Reason for this layout" htmlFor="setup-reason" required>
            <Textarea id="setup-reason" value={reason} onChange={onReasonChange} required />
          </FormField>
          {error && <div className="tm-picker-notice tm-picker-notice--danger"><AlertCircle className="h-4 w-4" />{error}</div>}
          {success && <div className="tm-picker-notice tm-picker-notice--success"><CheckCircle2 className="h-4 w-4" />{success}</div>}
          {successAction}
          <Button
            className="w-full"
            onClick={onApply}
            disabled={submitting || mixedConfirmedClasses || !selectedVehicleCount || !activeLayout}
          >
            {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Settings2 className="h-4 w-4" />}
            {submitting ? 'Applying…' : 'Apply tyre layout'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

SetupLayoutPicker.propTypes = {
  layouts: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  loadError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selectedClass: PropTypes.string,
  selectedLayout: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedVehicleCount: PropTypes.number.isRequired,
  mixedConfirmedClasses: PropTypes.bool,
  reason: PropTypes.string.isRequired,
  error: PropTypes.string,
  success: PropTypes.string,
  successAction: PropTypes.node,
  submitting: PropTypes.bool,
  onClassSelect: PropTypes.func.isRequired,
  onLayoutSelect: PropTypes.func.isRequired,
  onReasonChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onRetryLoad: PropTypes.func.isRequired,
};

export default SetupLayoutPicker;
